import { useEffect, useRef, useState } from 'react';

export type DeepLinkTargetKey = string | number;

export interface DeepLinkScrollAndFlashConfig {
	/** Gate: only act once the underlying data has loaded (e.g. !fetching, walkthrough present). */
	active: boolean;
	/**
	 * Resolve the row/note to scroll to and flash, or null when there's nothing to do. Called inside
	 * the effect (not during render) so reads with side effects — e.g. sessionStorage — happen once.
	 */
	resolveTargetKey: () => DeepLinkTargetKey | null;
	/** Locate the DOM node to scroll into view for a resolved key. */
	getElement: (key: DeepLinkTargetKey) => Element | null;
	/**
	 * One-time side effects when a target resolves: expand its row, consume the deep-link URL param so a
	 * later re-fetch doesn't re-trigger, clear the remembered position, etc. Runs before the scroll.
	 * Note: consuming the URL param here (history.replace) intentionally does NOT cancel the pending
	 * scroll/flash — see the unmount-only cleanup below.
	 */
	onActivate?: (key: DeepLinkTargetKey) => void;
	/** Extra values (besides `active`) that should re-run the resolve/scroll/flash, mirroring useEffect deps. */
	deps?: ReadonlyArray<unknown>;
	/** Delay before scrolling, giving the target time to render/expand. */
	scrollDelayMs?: number;
	/** How long the flash marker stays set. */
	flashDurationMs?: number;
	scrollBlock?: ScrollLogicalPosition;
}

/**
 * Shared behavior for search-result deep links that scroll a matched row/note into view and briefly
 * flash it (…/details?taskTitleId=… , …/notes?noteId=…). Centralizes the scroll/flash timing,
 * one-time activation, and cleanup so each consumer only supplies how to resolve the target, find its
 * element, and what to do on activation. Returns the key currently flashing (or null) so the caller can
 * mark the matching row/note while it renders.
 *
 * Each distinct resolved key is handled at most once per mount (tracked via a ref). This is what keeps a
 * later re-fetch — which re-runs the effect — from re-triggering the scroll, and it lets `onActivate`
 * consume the deep-link URL param (history.replace) without the resulting `location` change cancelling
 * the in-flight scroll: the scroll/flash timers live in refs and are cleared only on unmount, never on a
 * dep-change re-run.
 */
export function useDeepLinkScrollAndFlash(
	config: DeepLinkScrollAndFlashConfig
): DeepLinkTargetKey | null {
	const {
		active,
		deps = [],
		scrollDelayMs = 350,
		flashDurationMs = 3500,
		scrollBlock = 'center',
	} = config;
	const [flashedKey, setFlashedKey] = useState<DeepLinkTargetKey | null>(null);

	// Keep the callbacks in a ref so only `active`/`deps` drive re-runs — the callbacks are recreated
	// every render but their identity shouldn't re-trigger the scroll/flash.
	const callbacksRef = useRef(config);
	callbacksRef.current = config;

	const handledKeyRef = useRef<DeepLinkTargetKey | null>(null);
	const scrollTimerRef = useRef<ReturnType<typeof setTimeout>>();
	const flashTimerRef = useRef<ReturnType<typeof setTimeout>>();

	// Clear pending timers only on unmount — NOT on every dep change. Consuming the deep-link URL param
	// (history.replace) changes `location`, which re-runs the main effect; cancelling the timers there
	// would kill the scroll before it fires (the notes path works today only because it never consumes
	// its param).
	useEffect(
		() => () => {
			clearTimeout(scrollTimerRef.current);
			clearTimeout(flashTimerRef.current);
		},
		[]
	);

	useEffect(() => {
		if (!active) {
			return;
		}
		const { resolveTargetKey, onActivate } = callbacksRef.current;
		const targetKey = resolveTargetKey();
		if (targetKey === null || targetKey === undefined) {
			return;
		}
		// Already scrolled to this target this mount — don't re-trigger on a re-fetch or on the location
		// change caused by consuming the URL param.
		if (targetKey === handledKeyRef.current) {
			return;
		}
		handledKeyRef.current = targetKey;

		if (onActivate) {
			onActivate(targetKey);
		}

		clearTimeout(scrollTimerRef.current);
		clearTimeout(flashTimerRef.current);
		scrollTimerRef.current = setTimeout(() => {
			const element = callbacksRef.current.getElement(targetKey);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: scrollBlock });
			}
		}, scrollDelayMs);
		setFlashedKey(targetKey);
		flashTimerRef.current = setTimeout(() => setFlashedKey(null), flashDurationMs);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active, scrollDelayMs, flashDurationMs, scrollBlock, ...deps]);

	return flashedKey;
}
